import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';
import CompanyDetail from './CompanyDetail';
import { stageLabel, STAGE_TONES, formatCurrency, formatNumber, formatAddress, formatPhone } from './format';

// Customer lifecycle pipeline as a drag-and-drop Kanban. Dragging a company to a
// different stage applies a manual override (recorded in stage history).
const PIPELINE_FILTERS_KEY = 'crm_pipeline_filters';
const readSavedFilters = () => {
  try {
    return JSON.parse(window.localStorage.getItem(PIPELINE_FILTERS_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
};

const CompanyPipelineBoard = ({ meta }) => {
  const saved = readSavedFilters();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [accountType, setAccountType] = useState(saved.accountType || '');
  const [salesperson, setSalesperson] = useState(saved.salesperson || '');
  const [region, setRegion] = useState(saved.region || '');
  const [search, setSearch] = useState(saved.search || '');
  const [searchTerm, setSearchTerm] = useState(saved.search || '');
  const [extra, setExtra] = useState({});
  const [moreLoading, setMoreLoading] = useState({});
  const emptyProspect = {
    business_name: '',
    account_type: 'business',
    city: '',
    state: '',
    main_phone: '',
    website: '',
    contact_first: '',
    contact_last: '',
    contact_email: '',
    contact_phone: ''
  };
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prospect, setProspect] = useState(emptyProspect);

  // Debounce the free-text search so we don't refetch on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Persist the active filters so they survive refresh / re-login.
  useEffect(() => {
    try {
      window.localStorage.setItem(
        PIPELINE_FILTERS_KEY,
        JSON.stringify({ accountType, salesperson, region, search })
      );
    } catch (e) {
      /* ignore storage errors */
    }
  }, [accountType, salesperson, region, search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (accountType) params.account_type = accountType;
    if (salesperson) params.salesperson = salesperson;
    if (region) params.state = region;
    if (searchTerm) params.search = searchTerm;
    crmClient
      .companyBoard(params)
      .then(payload => {
        setBoard(payload);
        setExtra({});
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load the pipeline.');
        setLoading(false);
      });
  }, [accountType, salesperson, searchTerm, region]);

  useEffect(() => {
    load();
  }, [load]);

  const salespeople = (meta && meta.salespeople) || [];
  const ownerLabel = auth0Id => {
    if (!auth0Id) return 'Unassigned';
    const match = salespeople.find(sp => sp.auth0_id === auth0Id);
    return match ? match.email : auth0Id;
  };

  const stages = (board && board.stages) || [];
  const counts = (board && board.counts) || {};
  const columns = (board && board.columns) || {};

  // Fetch the next page of companies for a single stage column (respects filters).
  const loadMore = stage => {
    const shown = (columns[stage] || []).length + (extra[stage] || []).length;
    setMoreLoading(prev => ({ ...prev, [stage]: true }));
    const params = { stage, limit: 100, offset: shown, sort_by: 'lifetime_revenue', sort_dir: 'DESC' };
    if (accountType) params.account_type = accountType;
    if (salesperson) params.salesperson = salesperson;
    if (region) params.state = region;
    if (searchTerm) params.search = searchTerm;
    crmClient
      .listCompanies(params)
      .then(payload => {
        const more = (payload && payload.companies) || [];
        setExtra(prev => ({ ...prev, [stage]: [...(prev[stage] || []), ...more] }));
        setMoreLoading(prev => ({ ...prev, [stage]: false }));
      })
      .catch(() => setMoreLoading(prev => ({ ...prev, [stage]: false })));
  };

  const addProspect = () => {
    if (!prospect.business_name.trim()) {
      setError('A company name is required.');
      return;
    }
    setSaving(true);
    const hasContact = prospect.contact_first || prospect.contact_last || prospect.contact_email || prospect.contact_phone;
    crmClient
      .createCompany({
        business_name: prospect.business_name.trim(),
        crm_stage: 'prospect',
        account_type: prospect.account_type,
        city: prospect.city || null,
        state: prospect.state || null,
        main_phone: prospect.main_phone || null,
        website: prospect.website || null,
        lead_source: 'Manual entry',
        contact: hasContact
          ? {
              first_name: prospect.contact_first || null,
              last_name: prospect.contact_last || null,
              email: prospect.contact_email || null,
              phone: prospect.contact_phone || null
            }
          : undefined
      })
      .then(() => {
        setSaving(false);
        setShowAdd(false);
        setProspect(emptyProspect);
        setError(null);
        load();
      })
      .catch(err => {
        setSaving(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to add prospect.');
      });
  };

  const handleDrop = (stage, fromStage) => {
    setDragOverStage(null);
    const id = dragId;
    setDragId(null);
    if (!id || stage === fromStage) {
      return;
    }
    crmClient
      .setStage(id, { stage, reason: 'Moved on pipeline board' })
      .then(() => load())
      .catch(err => setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to move account.'));
  };

  if (loading && !board) {
    return <div className="crm-empty">Loading pipeline…</div>;
  }
  if (error && !board) {
    return <div className="crm-empty crm-empty--error">{error}</div>;
  }

  return (
    <div className={`crm-lifecycle${selectedId ? ' crm-lifecycle--split' : ''}`}>
      <div className="crm-lifecycle-main">
        <div className="crm-filters">
          <input
            type="search"
            className="crm-search-input"
            placeholder="Search company, city, or phone…"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          <button type="button" className="crm-btn crm-btn--sm" onClick={() => { setError(null); setShowAdd(true); }}>
            + Add prospect
          </button>
          <label className="crm-date-filter">
            <span>Owner</span>
            <select value={salesperson} onChange={event => setSalesperson(event.target.value)}>
              <option value="">All owners</option>
              <option value="unassigned">Unassigned</option>
              {salespeople.map(sp => (
                <option key={sp.auth0_id} value={sp.auth0_id}>
                  {sp.email}
                </option>
              ))}
            </select>
          </label>
          <label className="crm-date-filter">
            <span>Account type</span>
            <select value={accountType} onChange={event => setAccountType(event.target.value)}>
              <option value="">All accounts</option>
              <option value="business">Business</option>
              <option value="individual">Individual</option>
            </select>
          </label>
          <label className="crm-date-filter">
            <span>Region</span>
            <select value={region} onChange={event => setRegion(event.target.value)}>
              <option value="">All regions</option>
              {((meta && meta.states) || []).map(st => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </label>
          {(search || salesperson || accountType || region) && (
            <button
              type="button"
              className="crm-btn crm-btn--ghost crm-btn--sm"
              onClick={() => {
                setSearch('');
                setSalesperson('');
                setAccountType('');
                setRegion('');
              }}
            >
              Clear
            </button>
          )}
        </div>
        {loading && <p className="crm-muted">Updating…</p>}
        <p className="crm-muted">Drag an account between stages to set its lifecycle manually. Click a card to open it.</p>
        <div className="crm-board">
          {stages.map(stage => {
            const items = [...(columns[stage] || []), ...(extra[stage] || [])];
            const total = counts[stage] || 0;
            return (
              <div
                key={stage}
                className={`crm-board-col${dragOverStage === stage ? ' crm-board-col--over' : ''}`}
                onDragOver={event => {
                  event.preventDefault();
                  if (dragOverStage !== stage) setDragOverStage(stage);
                }}
                onDragLeave={() => setDragOverStage(prev => (prev === stage ? null : prev))}
                onDrop={event => handleDrop(stage, event.dataTransfer.getData('fromStage'))}
              >
                <div className={`crm-board-col-head crm-board-col-head--${STAGE_TONES[stage] || 'slate'}`}>
                  <span>{stageLabel(stage)}</span>
                  <span className="crm-board-count">{formatNumber(total)}</span>
                </div>
                <div className="crm-board-cards">
                  {items.map(company => (
                    <div
                      key={company.crm_company_id}
                      className="crm-board-card"
                      draggable
                      onDragStart={event => {
                        setDragId(company.crm_company_id);
                        event.dataTransfer.setData('fromStage', stage);
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={() => setSelectedId(company.crm_company_id)}
                    >
                      <strong>{company.business_name}</strong>
                      {formatAddress(company) ? <small className="crm-board-card-address">{formatAddress(company)}</small> : null}
                      {company.main_phone ? <small className="crm-board-card-address">{formatPhone(company.main_phone)}</small> : null}
                      <small>
                        {formatCurrency(company.lifetime_revenue)} · {formatNumber(company.lifetime_orders)} orders
                        {company.stage_override ? ' · manual' : ''}
                      </small>
                      <small className="crm-board-card-owner">{ownerLabel(company.assigned_salesperson_auth0_id)}</small>
                    </div>
                  ))}
                  {items.length === 0 && <div className="crm-board-empty">—</div>}
                  {total > items.length && (
                    <button
                      type="button"
                      className="crm-board-more"
                      disabled={moreLoading[stage]}
                      onClick={() => loadMore(stage)}
                    >
                      {moreLoading[stage]
                        ? 'Loading…'
                        : `Show more (${formatNumber(total - items.length)} more)`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedId && (
        <CompanyDetail companyId={selectedId} meta={meta} onClose={() => setSelectedId(null)} onChanged={load} />
      )}

      {showAdd && (
        <div className="crm-modal" role="dialog" aria-modal="true">
          <div className="crm-modal-card">
            <h3>Add prospect</h3>
            {error && <div className="crm-inline-error">{error}</div>}
            <label className="crm-field-grow">
              <span>Company name *</span>
              <input value={prospect.business_name} onChange={e => setProspect({ ...prospect, business_name: e.target.value })} />
            </label>
            <div className="crm-field-row">
              <label>
                <span>Type</span>
                <select value={prospect.account_type} onChange={e => setProspect({ ...prospect, account_type: e.target.value })}>
                  <option value="business">Business</option>
                  <option value="individual">Individual</option>
                </select>
              </label>
              <label>
                <span>City</span>
                <input value={prospect.city} onChange={e => setProspect({ ...prospect, city: e.target.value })} />
              </label>
              <label>
                <span>State</span>
                <input value={prospect.state} onChange={e => setProspect({ ...prospect, state: e.target.value })} />
              </label>
            </div>
            <div className="crm-field-row">
              <label className="crm-field-grow">
                <span>Phone</span>
                <input value={prospect.main_phone} onChange={e => setProspect({ ...prospect, main_phone: e.target.value })} />
              </label>
              <label className="crm-field-grow">
                <span>Website</span>
                <input value={prospect.website} onChange={e => setProspect({ ...prospect, website: e.target.value })} />
              </label>
            </div>
            <p className="crm-muted">Primary contact (optional)</p>
            <div className="crm-field-row">
              <label>
                <span>First name</span>
                <input value={prospect.contact_first} onChange={e => setProspect({ ...prospect, contact_first: e.target.value })} />
              </label>
              <label>
                <span>Last name</span>
                <input value={prospect.contact_last} onChange={e => setProspect({ ...prospect, contact_last: e.target.value })} />
              </label>
            </div>
            <div className="crm-field-row">
              <label className="crm-field-grow">
                <span>Email</span>
                <input value={prospect.contact_email} onChange={e => setProspect({ ...prospect, contact_email: e.target.value })} />
              </label>
              <label className="crm-field-grow">
                <span>Contact phone</span>
                <input value={prospect.contact_phone} onChange={e => setProspect({ ...prospect, contact_phone: e.target.value })} />
              </label>
            </div>
            <div className="crm-detail-actions">
              <button type="button" className="crm-btn" disabled={saving || !prospect.business_name.trim()} onClick={addProspect}>
                {saving ? 'Adding…' : 'Add to pipeline'}
              </button>
              <button
                type="button"
                className="crm-btn crm-btn--ghost"
                onClick={() => {
                  setShowAdd(false);
                  setProspect(emptyProspect);
                  setError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPipelineBoard;
